import { PASSWORD_MIN } from '@base/shared';
import { useMutation } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.ts';
import { usePlatformLook } from '../platform.tsx';
import { Button, Field, Input } from '../components/ui.tsx';
import { LanguageSwitch } from '../shell/LanguageSwitch.tsx';
import { SISTEMA_NOME } from '../sistema.ts';
import { readFields } from './login-form.ts';

/** Moldura das telas de fora do sistema (entrar, esqueci a senha, nova senha). */
function AuthCard({ title, body, children }: { title: string; body: string; children: ReactNode }) {
  const { t } = useTranslation();
  const { logoUrl } = usePlatformLook();
  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="flex w-full max-w-sm flex-col gap-6 rounded-lg border border-rule bg-sheet p-8 shadow-sm">
        <div className="flex flex-col gap-1">
          <img
            src={logoUrl}
            alt={SISTEMA_NOME}
            className="mb-3 max-h-16 w-auto max-w-40 object-contain object-left"
          />
          <h1 className="text-xl font-semibold">{title}</h1>
          <p className="text-sm text-muted">{body}</p>
        </div>
        {children}
        <Link
          to="/login"
          className="self-start text-sm text-muted underline-offset-2 hover:text-ink hover:underline"
        >
          {t('forgot.backToLogin')}
        </Link>
        <LanguageSwitch />
      </div>
    </div>
  );
}

function Alert({ tone, children }: { tone: 'ok' | 'danger'; children: ReactNode }) {
  return (
    <p
      role={tone === 'danger' ? 'alert' : 'status'}
      className={
        tone === 'danger'
          ? 'rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger'
          : 'rounded-lg border border-ok/30 bg-ok/5 p-3 text-sm text-ok'
      }
    >
      {children}
    </p>
  );
}

/** "Esqueci a senha" (ADR 0113): pede o link por e-mail. A resposta é sempre a mesma. */
export function ForgotPasswordPage() {
  const { t, i18n } = useTranslation();
  const [missing, setMissing] = useState(false);
  const request = useMutation({
    mutationFn: (email: string) =>
      api.post<void>('/auth/password/forgot', {
        email,
        locale: i18n.language === 'en' ? 'en' : 'pt-BR',
      }),
  });
  // Lido do formulário no envio: o navegador pode ter preenchido o e-mail
  // sem avisar a tela (veja login-form.ts).
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fields = readFields(e.currentTarget, ['email'] as const);
    setMissing(!fields);
    if (fields) request.mutate(fields.email);
  };
  return (
    <AuthCard title={t('forgot.title')} body={t('forgot.body')}>
      {request.isSuccess ? (
        <Alert tone="ok">{t('forgot.sent')}</Alert>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          {missing && <Alert tone="danger">{t('forgot.missing')}</Alert>}
          {request.isError && <Alert tone="danger">{t('auth.errors.unavailable')}</Alert>}
          <Field label={t('auth.email')}>
            <Input type="email" name="email" autoComplete="username" required autoFocus />
          </Field>
          <Button type="submit" className="py-2.5" disabled={request.isPending}>
            {request.isPending ? t('forgot.sending') : t('forgot.submit')}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}

/** Nova senha a partir do link do e-mail. */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { token } = useSearch({ from: '/reset-password' });
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const reset = useMutation({
    mutationFn: () => api.post<void>('/auth/password/reset', { token, newPassword }),
  });
  const mismatch = confirm !== '' && confirm !== newPassword;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!mismatch) reset.mutate();
  };
  if (!token) {
    return (
      <AuthCard title={t('reset.title')} body={t('reset.body')}>
        <Alert tone="danger">{t('reset.invalid')}</Alert>
      </AuthCard>
    );
  }
  return (
    <AuthCard title={t('reset.title')} body={t('reset.body')}>
      {reset.isSuccess ? (
        <>
          <Alert tone="ok">{t('reset.done')}</Alert>
          <Link
            to="/login"
            className="ui-control inline-flex items-center justify-center rounded-md bg-accent px-4 py-2.5 text-sm font-medium text-on-accent shadow-sm hover:opacity-90"
          >
            {t('auth.signIn')}
          </Link>
        </>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          {reset.isError && <Alert tone="danger">{t('reset.invalid')}</Alert>}
          <Field label={t('password.new')} hint={t('password.newHint', { min: PASSWORD_MIN })}>
            <Input
              type="password"
              name="new-password"
              autoComplete="new-password"
              minLength={PASSWORD_MIN}
              autoFocus
              value={newPassword}
              onChange={(e) => setNew(e.target.value)}
            />
          </Field>
          <Field
            label={t('password.confirm')}
            error={mismatch ? t('password.mismatch') : undefined}
          >
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>
          <Button
            type="submit"
            className="py-2.5"
            disabled={
              reset.isPending || newPassword.length < PASSWORD_MIN || confirm !== newPassword
            }
          >
            {reset.isPending ? t('common.saving') : t('reset.submit')}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
